import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, Loader2, Check, Send } from 'lucide-react';
import { useSendFeedback, useFeedbackList } from '../../hooks/useFeedback';

export default function ClientFeedbackPage() {
  const { t } = useTranslation();
  const send = useSendFeedback();
  const { data } = useFeedbackList();
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const canSend = message.trim().length > 0 && message.trim().length <= 4000 && !send.isPending;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await send.mutateAsync({ message: message.trim(), page: window.location.pathname });
      setMessage('');
      setSent(true);
    } catch {
      /* error state shown below */
    }
  };

  const history = data?.feedback ?? [];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquarePlus size={24} className="text-teal-600" /> {t('portal.feedback.title')}
        </h1>
        <p className="text-slate-500 mt-1">
          {t('portal.feedback.subtitle')}
        </p>
      </div>

      {sent ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <Check size={26} className="text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{t('portal.feedback.thanksTitle')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('portal.feedback.thanksBody')}</p>
          </div>
          <button
            onClick={() => setSent(false)}
            className="text-sm font-medium text-teal-600 hover:text-teal-700"
          >
            {t('portal.feedback.sendAnother')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-3">
          <label className="block text-sm font-medium text-slate-600">{t('portal.feedback.yourMessage')}</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder={t('portal.feedback.placeholder')}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-400">{message.trim().length}/4000</span>
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="flex items-center gap-2 py-2.5 px-5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {send.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {t('portal.feedback.sendButton')}
            </button>
          </div>
          {send.isError && (
            <p className="text-sm text-rose-600">{t('portal.feedback.errorSend')}</p>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-700">{t('portal.feedback.previousMessages')}</h2>
          <ul className="space-y-2">
            {history.map(item => (
              <li key={item.id} className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.message}</p>
                <p className="text-xs text-slate-400 mt-2">{item.created_at}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
